import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import { v4 as uuid } from 'uuid';
import { enhanceGLBridge, filterBridge, fixZipResUrl, makeupBridge } from '@meitu/wasm-lib';
import assetsConfig from '@meitu/wasm-lib/assetsConfig.json';
import { getFramework, IFramework, parseLayerId } from '@/framework';
import { type MainLayer } from '@/layers/main-layer';
import { base64Kit } from '@/utils/base64-kit';
import { createCanvas, getImgInfoAsync } from '@/utils/file-kit';
import { createFromCanvas } from '@/utils/layer-kit';
import { IImageLayerProps, ILayerProps } from '@/version-types';
import { getImageEnhanceState, IStore, watchImageEnhanceChange } from '../states/image-enhance-state';
import { IMakeUpEnhance, IMakeUpFilter } from '../types';
import { getHslArgs, getPosterizeArgs } from '../utils';
import { adapterEnhanceArgs, applyMakeUpResult } from '../utils/enhance';

const assets = {
    id: 'Enhance',
    zip: assetsConfig.filter_Enhance,
};

const filterAssets = {
    filterPath: 'filter/configuration_filter.plist',
};

export class MakeupImageEnhance {
    /** 编辑器实例 */
    private framework: IFramework;
    private store: IStore;

    constructor(identity: string) {
        this.framework = getFramework(identity);
        this.store = getImageEnhanceState(identity);

        watchImageEnhanceChange(identity, (state) => {
            this.store = state;
        });
    }

    private tryGetMainLayer() {
        const mainLayer = this.framework.getInstance<MainLayer>('MainLayer');
        if (!mainLayer) {
            throw new Error('MainLayer 实例不存在');
        }
        return mainLayer;
    }

    async create(
        _originLayer: ILayerProps,
        params: {
            filterID: string;
            filterURL: string;
            filterPath: string;
            isNeedInit: boolean;
            alpha: number;
            enhanceData: any;
        } = {
            filterID: '',
            filterURL: '',
            isNeedInit: true,
            filterPath: filterAssets.filterPath,
            alpha: 1,
            enhanceData: {},
        },
    ) {
        const { filterID, filterURL, isNeedInit, filterPath, enhanceData } = params;
        const originLayer = await this.tryGetMainLayer().enterMergeMode(_originLayer);

        this.store.setOriginLayer(originLayer);
        makeupBridge.setFilterBridgeInfo({
            order: 1,
            bridge: filterBridge,
            filterID,
            filterURL,
            alpha: 1,
            isNeedInit,
            filterPath,
        });

        makeupBridge.setEnhanceGLBridgeInfo({
            bridge: enhanceGLBridge,
            order: 0,
            filterID: assets.id,
            filterURL: fixZipResUrl(assets.zip),
            params: {
                ...enhanceData,
            },
            enhanceHSLMode: 0,
            isNeedInit: true,
        });

        const url = (originLayer.data as IImageLayerProps).url;
        const fromCanvas = await createFromCanvas(originLayer.id, url);

        await makeupBridge.from(fromCanvas).apply();
    }

    async resetEnhance() {
        this.store.resetEnhance();
    }

    async reset(pushHistory = true, trackFunc?: string): Promise<void> {
        const { framework, store: enhanceState } = this;
        const originIndex = framework.stage.getActiveOriginIndex();
        const originLayer = enhanceState.originLayer;
        const oldLayer = framework.getMainLayer(originIndex);
        const newLayer = cloneDeep(originLayer);

        if (!newLayer || !oldLayer) return;

        newLayer.__extendedState = {
            ...newLayer.__extendedState,
            makeupPresetId: undefined,
        };

        framework.stage.addOrUpdate(newLayer);

        if (!pushHistory) return;

        this.framework.history.write(oldLayer, newLayer, trackFunc);
    }

    async batchReset(pushHistory = true, trackFunc?: string): Promise<void> {
        if (this.store.lastOriginLayers.length === 0) {
            return;
        }

        for (let i = 0; i < this.store.originLayers.length; i++) {
            const mainLayer = this.store.originLayers[i];
            if (!mainLayer) {
                continue;
            }

            if (i === 0) {
                mainLayer.__extendedState = {
                    ...mainLayer.__extendedState,
                    makeupPresetId: undefined,
                };
            }

            this.framework.stage.addOrUpdate(cloneDeep(mainLayer));
        }

        if (pushHistory) {
            this.framework.history.write(
                cloneDeep(this.store.lastOriginLayers),
                cloneDeep(this.store.originLayers),
                trackFunc,
            );
        }
        this.store.setLastOriginLayers(cloneDeep(this.store.originLayers));
    }

    private async setFilter(filter: IMakeUpFilter) {
        console.log('setFilter', filter);
        makeupBridge.setFilterBridgeInfo(filter as any);
    }

    private async setEnhance(params: IMakeUpEnhance) {
        const { store: enhanceState } = this;
        const { baseEnhance, light, color, particulars } = params;
        const baseData = enhanceState.baseEnhance;
        const colorData = enhanceState.colorEnhance;
        const lightData = enhanceState.lightEnhance;
        const particularsData = enhanceState.particularsEnhance;
        const posterizeData = enhanceState.posterizeEnhance;
        const hslData = enhanceState.hslEnhance;
        const args = {};

        merge(
            args,
            baseData,
            lightData,
            colorData,
            particularsData,
            getPosterizeArgs(posterizeData),
            getHslArgs(hslData),
            baseEnhance,
            { light },
            { color },
            { particulars },
        );
        console.log('setEnhance Action', { args, params });
        const adapterParams = adapterEnhanceArgs(args);
        makeupBridge.setEnhanceGLBridgeInfo({ params: adapterParams } as any);
    }

    // FIXME: 首次点击预设调色没有效果
    async action(
        preset: {
            enhance: IMakeUpEnhance;
            filter: IMakeUpFilter;
            id: number;
        },
        pushHistory = false,
        trackFunc?: string,
    ): Promise<void> {
        const { framework } = this;
        const originIndex = framework.stage.getActiveOriginIndex();

        // TODO: 支持非主图的场景
        const oldLayer = framework.getMainLayer(originIndex)!;
        const tempLayer = cloneDeep(oldLayer);

        tempLayer.__isDoEnhance = true;
        tempLayer.__renderEnhanceCanvas = true;

        tempLayer.__extendedState = {
            ...tempLayer.__extendedState,
            makeupPresetId: preset.id,
        };

        framework.stage.addOrUpdate(tempLayer);

        if (preset.enhance) this.setEnhance(preset.enhance);
        if (preset.filter) this.setFilter(preset.filter);

        const finalLayer = cloneDeep(tempLayer);

        await applyMakeUpResult(finalLayer);

        framework.stage.addOrUpdate(finalLayer);

        const mergeResult = this.tryGetMainLayer().applyMergeMode(finalLayer, trackFunc);

        if (mergeResult || !pushHistory) return;

        this.framework.history.write(oldLayer, finalLayer, trackFunc);
    }

    async batchAction(
        preset: {
            enhance: IMakeUpEnhance;
            filter: IMakeUpFilter;
            id: number;
        },
        pushHistory = false,
        trackFunc?: string,
        onStageLoading?: (stageIndex: number, loadingInfo: { loading: boolean; text: string }) => void,
    ): Promise<void> {
        const { framework } = this;
        const newLayers = [] as ILayerProps[];
        const oldLayers = [] as ILayerProps[];
        for (let i = 0; i < this.store.originLayers.length; i++) {
            const mainLayer = cloneDeep(this.store.originLayers[i]);
            if (!mainLayer) {
                continue;
            }
            if (i === 0) {
                mainLayer.__extendedState = {
                    ...mainLayer.__extendedState,
                    makeupPresetId: preset.id,
                };
            }
            oldLayers.push(cloneDeep(this.store.lastOriginLayers[i] || cloneDeep(mainLayer)));
            const { naturalHeight, naturalWidth, imageTransform } = mainLayer.data as IImageLayerProps;
            const { img } = await getImgInfoAsync((mainLayer.data as IImageLayerProps).url);
            console.log('===>batchAction', (mainLayer.data as IImageLayerProps).url);
            const { canvas } = createCanvas(
                naturalWidth * imageTransform.scale,
                naturalHeight * imageTransform.scale,
                img,
            );
            await makeupBridge.from(canvas);
            if (preset.enhance) {
                this.setEnhance(preset.enhance);
            }
            if (preset.filter) {
                this.setFilter(preset.filter);
            }

            const { canvas: resultCanvas } = await makeupBridge.apply();

            if (resultCanvas) {
                const base64 = resultCanvas.toDataURL();
                const data = mainLayer.data as IImageLayerProps;

                data.url = base64Kit.getCode(base64, uuid());
            }
            newLayers.push(cloneDeep(mainLayer));
            framework.stage.addOrUpdate(cloneDeep(mainLayer));
            onStageLoading?.(parseLayerId(mainLayer.id)?.originIndex || 0, { loading: false, text: '' });
        }

        if (pushHistory) {
            this.store.setLastOriginLayers(cloneDeep(newLayers));
            this.framework.history.write(oldLayers, newLayers, trackFunc);
        }
    }
}
